// src/services/firestoreService.js
import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc,
  deleteDoc, query, where, orderBy, limit, serverTimestamp,
  onSnapshot, increment, setDoc,
} from "firebase/firestore";
import { db } from "./firebase";

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const col = (name) => collection(db, name);
const snap2arr = (snapshot) => snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

// ─── CATEGORIES ──────────────────────────────────────────────────────────────
export const getCategories = async () => {
  const q = query(col("categories"), orderBy("createdAt", "desc"));
  return snap2arr(await getDocs(q));
};
export const addCategory = (data) =>
  addDoc(col("categories"), { ...data, createdAt: serverTimestamp() });
export const updateCategory = (id, data) => updateDoc(doc(db, "categories", id), data);
export const deleteCategory = (id) => deleteDoc(doc(db, "categories", id));

// ─── PRODUCTS ────────────────────────────────────────────────────────────────
export const getProducts = async (filters = {}) => {
  let q = col("products");
  const constraints = [orderBy("createdAt", "desc")];
  if (filters.categoryId) constraints.unshift(where("categoryId", "==", filters.categoryId));
  if (filters.featured)   constraints.unshift(where("featured", "==", true));
  q = query(col("products"), ...constraints);
  return snap2arr(await getDocs(q));
};
export const getProductById = async (id) => {
  const snap = await getDoc(doc(db, "products", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};
export const addProduct = (data) =>
  addDoc(col("products"), { ...data, createdAt: serverTimestamp(), sold: 0 });
export const updateProduct = (id, data) => updateDoc(doc(db, "products", id), data);
export const deleteProduct = (id) => deleteDoc(doc(db, "products", id));

// ─── BANNERS / SLIDERS ───────────────────────────────────────────────────────
export const getBanners = async () => {
  const q = query(col("banners"), orderBy("order", "asc"));
  return snap2arr(await getDocs(q));
};
export const addBanner = (data) =>
  addDoc(col("banners"), { ...data, createdAt: serverTimestamp() });
export const updateBanner = (id, data) => updateDoc(doc(db, "banners", id), data);
export const deleteBanner = (id) => deleteDoc(doc(db, "banners", id));

// ─── BLOGS ───────────────────────────────────────────────────────────────────
export const getBlogs = async (publishedOnly = true) => {
  const constraints = [orderBy("createdAt", "desc")];
  if (publishedOnly) constraints.unshift(where("published", "==", true));
  const q = query(col("blogs"), ...constraints);
  return snap2arr(await getDocs(q));
};
export const getBlogById = async (id) => {
  const snap = await getDoc(doc(db, "blogs", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};
export const addBlog = (data) =>
  addDoc(col("blogs"), { published: false, ...data, createdAt: serverTimestamp() });
export const updateBlog = (id, data) => updateDoc(doc(db, "blogs", id), data);
export const deleteBlog = (id) => deleteDoc(doc(db, "blogs", id));

// ─── CAREERS / JOBS ──────────────────────────────────────────────────────────
export const getJobs = async () => {
  const q = query(col("jobs"), orderBy("postedAt", "desc"));
  return snap2arr(await getDocs(q));
};
export const addJob = (data) =>
  addDoc(col("jobs"), { ...data, createdAt: serverTimestamp() });
export const updateJob = (id, data) => updateDoc(doc(db, "jobs", id), data);
export const deleteJob = (id) => deleteDoc(doc(db, "jobs", id));

// ─── ORDERS ──────────────────────────────────────────────────────────────────
export const ORDER_STATUSES = ["Pending", "Confirmed", "Shipped", "Delivered", "Cancelled"];

export const placeOrder = (orderData) =>
  addDoc(col("orders"), {
    ...orderData,
    status: "Pending",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

export const getOrdersByUser = async (userId) => {
  const q = query(col("orders"), where("userId", "==", userId), orderBy("createdAt", "desc"));
  return snap2arr(await getDocs(q));
};

export const getAllOrders = async () => {
  const q = query(col("orders"), orderBy("createdAt", "desc"));
  return snap2arr(await getDocs(q));
};

export const updateOrderStatus = (orderId, status) =>
  updateDoc(doc(db, "orders", orderId), { status, updatedAt: serverTimestamp() });

export const deleteOrder = (id) => deleteDoc(doc(db, "orders", id));
export const subscribeToOrders = (callback) =>
  onSnapshot(query(col("orders"), orderBy("createdAt", "desc")), (snap) =>
    callback(snap2arr(snap))
  );

// ─── REVIEWS ─────────────────────────────────────────────────────────────────
// One review per (order, product) pair. Using a deterministic doc id means
// re-submitting a review from the same order/product UPDATES the existing
// review (merge: true) instead of creating a duplicate — so "Add Review" and
// "Edit Review" can both just call this same function.
export const addReview = async (orderId, productId, data) => {
  await setDoc(
    doc(db, "reviews", `${orderId}_${productId}`),
    {
      ...data,
      orderId,
      productId,
      createdAt: serverTimestamp(),
    },
    { merge: true }
  );
  // Keep the product doc's rating/reviews in sync so ProductCard (grid view)
  // can show real numbers with a single read, instead of querying reviews
  // separately for every card.
  await syncProductRatingAggregate(productId);
};

// Recomputes average rating + review count for a product from its actual
// review docs, and caches the result on products/{productId}.
const syncProductRatingAggregate = async (productId) => {
  const reviews = await getProductReviews(productId);
  const count = reviews.length;
  const avg = count > 0
    ? reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / count
    : 0;
  await updateDoc(doc(db, "products", productId), {
    rating: Math.round(avg * 10) / 10, // one decimal place
    reviews: count,
  });
};

// All reviews a user has already left for a specific order (used to prefill
// "already reviewed" state on the Order Detail page). No orderBy here so it
// doesn't need a composite index.
export const getReviewsForOrder = async (orderId) => {
  const q = query(col("reviews"), where("orderId", "==", orderId));
  return snap2arr(await getDocs(q));
};

// All reviews for a given product (e.g. to show on the product page).
// NOTE: where + orderBy on different fields needs a Firestore composite
// index — Firestore will log a console link to auto-create it on first run.
export const getProductReviews = async (productId) => {
  const q = query(col("reviews"), where("productId", "==", productId), orderBy("createdAt", "desc"));
  return snap2arr(await getDocs(q));
};

// ─── USERS ───────────────────────────────────────────────────────────────────
export const createUserProfile = (uid, data) =>
  setDoc(
    doc(db, "users", uid),
    { role: "customer", ...data, createdAt: serverTimestamp() },
    { merge: true }
  );

export const getUserProfile = async (uid) => {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getAllUsers = async () => {
  const q = query(col("users"), orderBy("createdAt", "desc"));
  return snap2arr(await getDocs(q));
};

// setDoc + merge:true creates the doc if it doesn't exist yet (e.g. a user
// who signed up before createUserProfile was in place), and only touches the
// given fields if it does — so it's safe to call for any profile write.
export const updateUserProfile = (uid, data) =>
  setDoc(doc(db, "users", uid), data, { merge: true });

// ─── SAVED ADDRESSES ─────────────────────────────────────────────────────────
// Stored as a plain array field (`addresses`) on the user's own users/{uid}
// doc — a customer only ever needs their own short list, so a subcollection
// would be overkill. Each entry looks like:
//   { id, label, address, city, state, pincode, landmark, isDefault }
// There's no atomic "update one array element" op in Firestore, so each of
// these reads the current array, edits it in JS, then writes the whole
// array back.
export const addUserAddress = async (uid, address) => {
  const profile = await getUserProfile(uid);
  const addresses = profile?.addresses || [];
  const newAddress = {
    id: `addr_${Date.now()}`,
    isDefault: addresses.length === 0, // first saved address becomes the default
    ...address,
  };
  await updateUserProfile(uid, { addresses: [...addresses, newAddress] });
  return newAddress;
};

export const updateUserAddress = async (uid, addressId, data) => {
  const profile = await getUserProfile(uid);
  const addresses = (profile?.addresses || []).map((a) =>
    a.id === addressId ? { ...a, ...data, id: addressId } : a
  );
  await updateUserProfile(uid, { addresses });
};

export const deleteUserAddress = async (uid, addressId) => {
  const profile = await getUserProfile(uid);
  const remaining = (profile?.addresses || []).filter((a) => a.id !== addressId);
  // If the deleted address was the default one, promote the next address
  // so there's always a default whenever addresses exist.
  if (remaining.length && !remaining.some((a) => a.isDefault)) {
    remaining[0] = { ...remaining[0], isDefault: true };
  }
  await updateUserProfile(uid, { addresses: remaining });
};

export const setDefaultUserAddress = async (uid, addressId) => {
  const profile = await getUserProfile(uid);
  const addresses = (profile?.addresses || []).map((a) => ({
    ...a,
    isDefault: a.id === addressId,
  }));
  await updateUserProfile(uid, { addresses });
};

// ─── ABOUT US ────────────────────────────────────────────────────────────────
export const getAboutContent = async () => {
  const snap = await getDoc(doc(db, "siteContent", "about"));
  return snap.exists() ? snap.data() : {};
};
export const updateAboutContent = (data) =>
  setDoc(doc(db, "siteContent", "about"), { ...data, updatedAt: serverTimestamp() });

// ─── CERTIFICATES ─────────────────────────────────────────────────────────────
export const getCertificates = async () => {
  const q = query(col("certificates"), orderBy("order", "asc"));
  return snap2arr(await getDocs(q));
};
export const addCertificate = (data) =>
  addDoc(col("certificates"), { ...data, createdAt: serverTimestamp() });
export const deleteCertificate = (id) => deleteDoc(doc(db, "certificates", id));

// ─── CONTACT INQUIRIES ────────────────────────────────────────────────────────
export const submitContactForm = (data) =>
  addDoc(col("contacts"), { ...data, read: false, createdAt: serverTimestamp() });

export const getContacts = async () => {
  const q = query(col("contacts"), orderBy("createdAt", "desc"));
  return snap2arr(await getDocs(q));
};
export const markContactRead = (id) => updateDoc(doc(db, "contacts", id), { read: true });
export const deleteContact = (id) => deleteDoc(doc(db, "contacts", id));

// ─── WISHLISTS ────────────────────────────────────────────────────────────────
export const addToWishlist = (userId, product) =>
  setDoc(doc(db, "wishlists", userId, "items", product.id), {
    productId: product.id,
    name: product.name,
    price: product.price,
    originalPrice: product.originalPrice || null,
    image: product.images?.[0] || "",
    unit: product.unit || "",
    weightSize: product.weightSize || null,
    packSize: product.packSize || null,
    pricePerKg: product.pricePerKg || null,
    pricePerPack: product.pricePerPack || null,
    rating: product.rating || null,
    reviews: product.reviews || null,
    hsnSac: product.hsnSac || "",
    category: product.category || "",
    inStock: product.inStock ?? true,
    addedAt: serverTimestamp(),
  });

export const removeFromWishlist = (userId, productId) =>
  deleteDoc(doc(db, "wishlists", userId, "items", productId));

export const getWishlist = async (userId) => {
  const q = query(
    collection(db, "wishlists", userId, "items"),
    orderBy("addedAt", "desc")
  );
  return snap2arr(await getDocs(q));
};

export const isInWishlist = async (userId, productId) => {
  const snap = await getDoc(doc(db, "wishlists", userId, "items", productId));
  return snap.exists();
};

// ─── DASHBOARD STATS ─────────────────────────────────────────────────────────
export const getDashboardStats = async () => {
  const [orders, products, users, contacts] = await Promise.all([
    getDocs(col("orders")),
    getDocs(col("products")),
    getDocs(col("users")),
    getDocs(query(col("contacts"), where("read", "==", false))),
  ]);
  const orderData = snap2arr(orders);
  const revenue = orderData
    .filter((o) => o.status !== "Cancelled")
    .reduce((sum, o) => sum + (o.total || 0), 0);
  const statusCounts = ORDER_STATUSES.reduce((acc, s) => {
    acc[s] = orderData.filter((o) => o.status === s).length;
    return acc;
  }, {});
  return {
    totalOrders:   orders.size,
    totalProducts: products.size,
    totalUsers:    users.size,
    unreadContacts: contacts.size,
    revenue,
    statusCounts,
  };
};

// ─── HOME PRODUCTS (featured first, then all) ────────────────────────────────
// Fetches ALL products and sorts featured ones to the top.
// This way the home screen always shows products even if none are marked featured.
export const getHomeProducts = async () => {
  const q = query(col("products"), orderBy("createdAt", "desc"));
  const all = snap2arr(await getDocs(q));
  // Put featured products first, then the rest
  const featuredFirst = [
    ...all.filter((p) => p.featured === true),
    ...all.filter((p) => p.featured !== true),
  ];
  return featuredFirst;
};

// ─── PHYSICAL ORDERS (in-store / offline bills generated from the Physical Bill page) ─
// sanitize(): Firestore's addDoc/setDoc REJECT any field whose value is
// `undefined` (it throws "Unsupported field value: undefined"), which is the
// most common reason a save silently fails. Round-tripping through
// JSON.stringify/parse strips every undefined key (and turns undefined array
// entries into null) so the payload is always safe to write.
const sanitize = (obj) => JSON.parse(JSON.stringify(obj));

export const getPhysicalOrders = async () => {
  const q = query(col("physicalOrders"), orderBy("createdAt", "desc"));
  return snap2arr(await getDocs(q));
};
export const addPhysicalOrder = (data) =>
  addDoc(col("physicalOrders"), {
    ...sanitize(data),
    type: "physical",
    createdAt: serverTimestamp(),
  });
export const deletePhysicalOrder = (id) => deleteDoc(doc(db, "physicalOrders", id));

// ─── TESTIMONIALS ────────────────────────────────────────────────────────────
const testimonialsRef = collection(db, "testimonials");

export const getTestimonials = async () => {
  const q = query(testimonialsRef, orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addTestimonial = async (data) => {
  return addDoc(testimonialsRef, {
    ...data,
    createdAt: serverTimestamp(),
  });
};

export const updateTestimonial = async (id, data) => {
  return updateDoc(doc(db, "testimonials", id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

export const deleteTestimonial = async (id) => {
  return deleteDoc(doc(db, "testimonials", id));
};

// ─── FAQs ────────────────────────────────────────────────────────────────────
const faqsCol = collection(db, "faqs");

// READ — all FAQs sorted by `order` (falls back to createdAt if order is missing)
export async function getFaqs() {
  const q = query(faqsCol, orderBy("order", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// CREATE
export async function addFaq(data) {
  return addDoc(faqsCol, {
    ...data,
    createdAt: serverTimestamp(),
  });
}

// UPDATE
export async function updateFaq(id, data) {
  return updateDoc(doc(db, "faqs", id), data);
}

// DELETE
export async function deleteFaq(id) {
  return deleteDoc(doc(db, "faqs", id));
}

// ─── FAQ ILLUSTRATION (side card next to FAQ accordion) ──────────────────────
const faqIllustrationRef = doc(db, "settings", "faqIllustration");

// READ — returns null if the admin hasn't saved one yet
export async function getFaqIllustration() {
  const snap = await getDoc(faqIllustrationRef);
  return snap.exists() ? snap.data() : null;
}

// CREATE / UPDATE (merge so partial saves never wipe other fields)
export async function updateFaqIllustration(data) {
  return setDoc(faqIllustrationRef, data, { merge: true });
}

// ─── SOCIAL LINKS (footer "Follow Us") ───────────────────────────────────────
// Doc shape: { platform: "facebook", label: "Facebook",
//              href: "https://facebook.com/...", order: 0 }
const socialLinksCol = collection(db, "socialLinks");

export async function getSocialLinks() {
  const q = query(socialLinksCol, orderBy("order", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addSocialLink(data) {
  return addDoc(socialLinksCol, {
    ...data,
    createdAt: serverTimestamp(),
  });
}

export async function updateSocialLink(id, data) {
  return updateDoc(doc(db, "socialLinks", id), data);
}

export async function deleteSocialLink(id) {
  return deleteDoc(doc(db, "socialLinks", id));
}

// ─── ORDER CHARGES (delivery/kg + SGST/CGST %) ───────────────────────────────
export const DEFAULT_ORDER_CHARGES = {
  deliveryPerKg: 0.5,
  sgstPercent: 2.5,
  cgstPercent: 2.5,
};

const orderChargesRef = doc(db, "settings", "orderCharges");

// READ — always returns a complete object, falling back to the defaults
// above for any field that hasn't been saved yet (or if the doc doesn't
// exist at all, e.g. the very first time the admin opens the app).
export async function getOrderCharges() {
  const snap = await getDoc(orderChargesRef);
  return snap.exists()
    ? { ...DEFAULT_ORDER_CHARGES, ...snap.data() }
    : { ...DEFAULT_ORDER_CHARGES };
}

// CREATE / UPDATE (merge so partial saves never wipe other fields)
export async function updateOrderCharges(data) {
  return setDoc(orderChargesRef, data, { merge: true });
}

// ─── JOBS (single job detail) ────────────────────────────────────────────────
export const getJobById = async (id) => {
  const snap = await getDoc(doc(db, "jobs", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// ─── CAREER CULTURE SECTION (the "Our Culture" block on the Careers page) ────
export const getCareerCulture = async () => {
  const snap = await getDoc(doc(db, "siteContent", "careerCulture"));
  return snap.exists() ? snap.data() : {};
};
export const updateCareerCulture = (data) =>
  setDoc(doc(db, "siteContent", "careerCulture"), { ...data, updatedAt: serverTimestamp() });

// ─── JOB APPLICATIONS (submitted from the CareerDetail "Apply for this role" form) ─
export const submitJobApplication = (jobId, data) =>
  addDoc(col("jobApplications"), {
    ...data,
    jobId,
    status: "New",
    createdAt: serverTimestamp(),
  });

export const getJobApplications = async (jobId) => {
  const q = query(col("jobApplications"), where("jobId", "==", jobId), orderBy("createdAt", "desc"));
  return snap2arr(await getDocs(q));
};

export const getAllJobApplications = async () => {
  const q = query(col("jobApplications"), orderBy("createdAt", "desc"));
  return snap2arr(await getDocs(q));
};

export const deleteJobApplication = (id) => deleteDoc(doc(db, "jobApplications", id));